<template>
  <section id="login-page">
    <el-form :model="formData" ref="ruleForm" @keyup.enter.prevent="submitForm('ruleForm')" class="signin-layout">
      <div class="signin-visual">
        <img src="@/assets/images/TestGo.svg" alt="TestGO Labs Portal" class="visual-logo" />
        <div class="visual-copy">
          <p class="eyebrow">LABORATORY MANAGEMENT PLATFORM</p>
          <h1><span>Simplifying</span><span>Laboratory Operations</span><span>for Better Care.</span></h1>
          <p class="visual-description">Manage orders, process tests, track results and collaborate seamlessly - built for modern laboratories.</p>
          <div class="feature-list">
            <div class="feature-item"><span class="feature-icon feature-icon-orders">+</span><div><strong>Manage Orders</strong><small>From accession to reporting</small></div></div>
            <div class="feature-item"><span class="feature-icon feature-icon-tests">&#9671;</span><div><strong>Process Tests</strong><small>Streamlined lab workflows</small></div></div>
            <div class="feature-item"><span class="feature-icon feature-icon-results">&#9646;</span><div><strong>Track Results</strong><small>Real-time visibility and insights</small></div></div>
            <div class="feature-item"><span class="feature-icon feature-icon-team">&#8226;</span><div><strong>Collaborate Securely</strong><small>Labs, providers and teams in sync</small></div></div>
          </div>
        </div>
        <div class="visual-footer"><span>ACCURACY</span><i></i><span>COMPLIANCE</span><i></i><strong>BETTER OUTCOMES</strong></div>
      </div>
      <div class="signin-panel-wrap">
        <div class="signin-panel">
          <img src="@/assets/images/TestGo.svg" alt="TestGO Labs Portal" class="panel-logo" />
          <h2>Welcome Back!</h2>
          <p class="panel-subtitle">Sign in to your Labs Portal to continue.</p>
          <div class="legacy-credentials" aria-hidden="true">
            <div class="username"><el-input class="user user-lower" v-model="formData.username" placeholder="Enter Username" /></div>
            <div class="show-icon"><el-input v-model="formData.password" :type="showPassword ? 'text' : 'password'" placeholder="Enter password" class="pswd" /></div>
            <el-button class="login-btn" type="primary" :loading="loading" :disabled="loading" @click="submitForm('ruleForm')">LOGIN</el-button>
          </div>
          <el-button class="microsoft-login-btn" :loading="microsoftLoading" :disabled="loading || microsoftLoading" @click="signInWithMicrosoft">
            <svg class="microsoft-logo" viewBox="0 0 21 21" width="21" height="21" aria-hidden="true">
              <path fill="#f25022" d="M1 1h9v9H1z" /><path fill="#7fba00" d="M12 1h9v9h-9z" /><path fill="#00a4ef" d="M1 12h9v9H1z" /><path fill="#ffb900" d="M12 12h9v9h-9z" />
            </svg>
            <span class="microsoft-label">Sign in with Microsoft</span><span class="button-arrow" aria-hidden="true">&#8594;</span>
          </el-button>
          <div class="secure-signin"><img src="@/assets/images/icons/security_shield_sso.svg" alt="" class="secure-icon" /><div><strong>Secure single sign-on</strong><small>Powered by Microsoft Entra ID</small></div></div>
          <p class="error">{{ this.displayErrorMessage }}</p>
          <p class="error invalid" v-if="getErrors && getErrors.critical_error">{{ this.getErrors.critical_error }}</p>
        </div>
      </div>
    </el-form>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import { TIMEOUT_REASON } from "@/helpers/idleSession";
export default {
  name: "Signin",
  computed: {
    signedOutForInactivity() {
      return this.$route.query.timeout === TIMEOUT_REASON;
    },
    ...mapGetters("auth", [
      "getGeneralErrorMessage",
      "getAuthenticationStatus",
      "getAuthenticatedUser",
    ]),
    ...mapGetters("errors", ["getErrors"]),
    displayErrorMessage() {
      if (
        this.getErrors &&
        this.getErrors.username &&
        this.getErrors.password
      ) {
        return "Username and Password are Required";
      } else if (this.getErrors && this.getErrors.username) {
        return "Username is Required";
      } else if (this.getErrors && this.getErrors.password) {
        return "Password is Required";
      }
      return "";
    },
  },
  data() {
    return {
      loading: false,
      microsoftLoading: false,
      showPassword: false,
      formData: {
        username: "",
        password: "",
        err: "",
      },
    };
  },
  mounted() {
    if (
      this.getErrors &&
      this.getErrors["critical_error"] &&
      this.getErrors["critical_error"] != "Session Timeout"
    ) {
      this.$store.dispatch("errors/clear");
    } else if (
      this.getErrors &&
      (this.getErrors.username || this.getErrors.password)
    ) {
      this.$store.dispatch("errors/clear");
    }

    this.toggleBodyClass("addClass", "signInPage");
  },
  beforeUnmount() {
    this.toggleBodyClass("removeClass", "signInPage");
  },
  methods: {
    async signInWithMicrosoft() {
      try {
        this.microsoftLoading = true;
        await this.$store.dispatch("auth/microsoftSignIn");
      } catch (error) {
        this.microsoftLoading = false;
        const message = error && error.message === "Microsoft SSO is not configured"
          ? "Add the Microsoft VUE_APP_* variables to the frontend .env file and restart the dev server."
          : "Unable to start Microsoft sign-in. Check the browser console for details.";
        this.$notify.error({
          title: "Microsoft sign-in failed",
          message,
        });
      }
    },

    convertToLowerCase(data = "") {
      return data.toLowerCase();
    },
    submitForm(formName) {
      try {
        this.$store.dispatch("session/updateSessionStatus", true);
        this.$refs[formName].validate((valid) => {
          if (valid) {
            this.signIn();
          } else {
            return false;
          }
        });
      } catch (error) {
        console.log(error);
      }
    },
    async signIn() {
      try {
        this.loading = true;

        const params = {
          username: this.formData.username?.trim().toLowerCase() || "",
          password: this.formData.password,
        };

        await this.$store.dispatch("auth/signin", params);

        this.loading = false;

        if (this.getAuthenticationStatus) {
          this.collectAndLogLocation();
          if (this.$route.query.redirect) {
            this.$router.push({
              name: this.$route.query.redirect,
              params: JSON.parse(this.$route.query.params),
              query: this.$route.query.query
                ? JSON.parse(this.$route.query.query)
                : {},
            });
          } else {
            this.goToDashboard();
          }
        } else {
          this.formData.err = this.getGeneralErrorMessage;
        }

      } catch (error) {
        this.loading = false;
        console.error(error);
      }
    },
    collectAndLogLocation() {
      const gather = async () => {
        let ip_address = null;
        let latitude = null;
        let longitude = null;
        let location_address = null;
        let location_source = "UNKNOWN";

        try {
          const ipRes = await fetch("https://api.ipify.org?format=json");
          const ipData = await ipRes.json();
          ip_address = ipData.ip;
          location_source = "IP_ONLY";
        } catch {
          // non-critical
        }

        if (navigator.geolocation) {
          try {
            const position = await new Promise((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, {
                timeout: 10000,
                maximumAge: 0,
              });
            });
            latitude = position.coords.latitude;
            longitude = position.coords.longitude;
            location_source = "GPS";

            try {
              const geoRes = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
              );
              const geoData = await geoRes.json();
              location_address = geoData.display_name || null;
            } catch {
              // non-critical
            }
          } catch {
            // GPS denied or unavailable
          }
        }

        await this.$store.dispatch("auth/logLocation", {
          ip_address,
          latitude,
          longitude,
          location_address: location_address || "Unknown Location",
          location_source,
        });
      };

      gather().catch(() => {});
    },
    goToDashboard() {
      let query = this.$route.query;

      if (query) {
        let redirect = query.redirect;
        let routerParams = query.params ? JSON.parse(query.params) : {};
        let routerQuery = query.query ? JSON.parse(query.query) : {};
        if (redirect) {
          this.$router.push({
            name: redirect,
            query: routerQuery,
            params: routerParams,
          });
        } else if (
          this.getAuthenticatedUser &&
          this.getAuthenticatedUser.user_type &&
          this.getAuthenticatedUser.user_type === "TOP_DOG"
        ) {
          this.$router.push("/labs");
        } else {
          this.$router.push("/");
        }
      } else {
        this.$router.push("/");
      }
    },
    toggleBodyClass(addRemoveClass, className) {
      const el = document.body;

      if (addRemoveClass === "addClass") {
        el.classList.add(className);
      } else {
        el.classList.remove(className);
      }
    },
  },
};
</script>

<style lang="scss">
#login-page {
  min-height: 100vh;
  background-color: #fff;
  background-image: url(/img/login-background.png);

  .timeout-notice {
    margin: 0 0 16px;
    padding: 10px 12px;
    border: 1px solid #f0d9a8;
    border-radius: 6px;
    background: #fff8e6;
    color: #6b4e00;
    font-size: 14px;
    line-height: 1.4;
    text-align: left;
  }

  .bg-image {
    img {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100vh;
      object-fit: cover;
      object-position: center;
    }
  }
  .company-logo {
    padding: 0px 0px 48px;
    // margin-bottom: 40px;
    text-align: center;
    img {
      width: 230px;
    }
  }
  .login-image {
    display: flex;
    align-items: center;
    min-height: 100vh;
  }
  .login-form {
    position: relative;
    .inner-wrap {
      position: relative;
      z-index: 10;
      max-width: 300px;
      padding: 3em 2em 3em;
      margin: 0 auto;
      background: linear-gradient(#ecf2f6, #ecf2f6) padding-box,
        linear-gradient(
            0deg,
            rgba(236, 242, 246, 1),
            rgba(179, 174, 227, 0.4427),
            rgba(230, 227, 255, 1)
          )
          border-box;
      border: 3px solid transparent;
      border-radius: 10%;
      backdrop-filter: blur(18.5px);
      box-shadow: 0px 12px 37px rgba(0, 0, 0, 0.19);

      @media screen and (min-width: 1700px) {
        max-width: 350px;
        padding: 5em 3em;
      }

      .username {
        position: relative;
        margin-bottom: 1.5em;
        .user {
          .el-input__inner {
            height: 40px;
            color: #555073;
            font-family: "Poppins", sans-serif;
            font-weight: 500;
            background: #ffffff;
            padding-left: 50px;
            font-size: 16px;
            border: 1px solid #ffffff;
            border-radius: 10px;
            text-transform: lowercase !important;
            &::placeholder {
              font-weight: 400;
              color: #555073;
              font-size: 16px;
            }
            @media screen and (min-width: 1700px) {
              height: 50px;
            }
          }
        }
      }
      .pswd {
        position: relative;
        .el-input__inner {
          height: 40px;
          color: #555073;
          font-family: "Poppins", sans-serif;
          font-weight: 500;
          padding-left: 50px;
          font-size: 16px;
          background: #ffffff;
          border: 1px solid #ffffff;
          border-radius: 10px;
          @media screen and (min-width: 1700px) {
            height: 50px;
          }
          &::placeholder {
            font-weight: 400;
            color: #555073;
            font-size: 16px;
          }
        }
        .el-input__suffix-inner {
          .el-input__clear {
            right: 40px;
            color: #3084ec;
          }
        }
      }
      .input-icon {
        position: absolute;
        left: 20px;
        top: 10px;
        @media screen and (min-width: 1700px) {
          top: 15px;
        }
        img {
          width: 13px;
          @media screen and (min-width: 1700px) {
            width: 14px;
          }
        }
      }
      .forgot {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin: -6px 0 14px 0;
        p {
          padding: 0;
          margin-bottom: -2px;
          margin-left: -5px;
          color: #2062ad;
          font-size: 12px;
          font-weight: 400;
        }
      }
      .login-btn {
        overflow: hidden;
        width: 100%;
        max-width: 100%;
        padding: 14px;
        margin-top: 3rem;
        color: white;
        font-size: 16px;
        background-color: rgba(33, 38, 88, 1);

        border: none;
        border-radius: 10px;
      }
      .login-divider {
        display: flex;
        align-items: center;
        gap: 10px;
        margin: 16px 0 6px;
        color: #8a8f98;
        font-size: 12px;
        font-weight: 400;
        text-transform: uppercase;
        letter-spacing: 0.08em;

        &::before,
        &::after {
          content: "";
          flex: 1;
          height: 1px;
          background: #d3d9e0;
        }
      }
      .microsoft-login-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        width: 100%;
        max-width: 100%;
        min-height: 48px;
        padding: 12px 16px;
        margin-top: 0;
        color: #1f1f1f;
        font-size: 15px;
        font-weight: 500;
        line-height: 1.2;
        letter-spacing: -0.01em;
        background-color: #ffffff;
        border: 1px solid #d9d9d9;
        border-radius: 12px;
        box-shadow:
          0 1px 1px rgba(0, 0, 0, 0.04),
          0 4px 12px rgba(0, 0, 0, 0.06);
        transition:
          background-color 0.18s ease,
          border-color 0.18s ease,
          box-shadow 0.18s ease,
          transform 0.18s ease;

        &:hover {
          color: #1f1f1f;
          background-color: #f5f5f5;
          border-color: #8f8f8f;
          box-shadow:
            0 2px 2px rgba(0, 0, 0, 0.05),
            0 8px 20px rgba(0, 0, 0, 0.1);
          transform: translateY(-1px);
        }

        &:focus,
        &:active {
          color: #1f1f1f;
        }

        &:focus-visible {
          outline: 3px solid rgba(0, 103, 192, 0.35);
          outline-offset: 2px;
        }

        &:active {
          background-color: #eaeaea;
          box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.08);
          transform: translateY(0);
        }

        &.is-disabled,
        &:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }

        &.is-loading {
          .microsoft-logo {
            opacity: 0.55;
          }
        }
      }
      .microsoft-logo {
        flex: 0 0 21px;
        width: 21px;
        height: 21px;
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05);
        border-radius: 2px;
      }
    }
  }
  .error {
    color: rgb(177, 29, 29) !important;
    font-size: 13px;
    text-align: left;
    margin-top: 3px;
    .pl-3 {
      padding-left: 3px;
    }
    &.invalid {
      text-align: center;
    }
  }
  .login-image {
    width: 90%;
    margin-left: auto;

    @media screen and (min-width: 1700px) {
      width: 80%;
      margin-left: auto;
    }
    @media (max-width: 991px) {
      display: none;
    }
  }
}
@media screen and (max-width: 375px) {
  .login-form {
    text-align: center;
    .inner-wrap {
      p {
        font-size: 13px !important;
      }
    }
    .forgot {
      display: inline !important;
      .el-checkbox {
        height: 20px;
      }
    }
  }
}
@media screen and (min-width: 1200px) {
  .login-form {
    margin: auto 0;
  }
}
</style>
<style lang="scss">
#login-page {
  &, * , *::before, *::after { box-sizing: border-box; }
  height: 100vh;
  height: 100dvh;
  min-height: 0;
  overflow: hidden;
  background: #f6fffb;
  .bg-image { display: none; }
  .signin-layout { display: flex; width: 100%; height: 100%; min-height: 0; }
  .signin-visual { position: relative; flex: 1 1 62%; height: 100%; min-height: 0; overflow: hidden; padding: clamp(2rem, 4vw, 3.5rem) clamp(2rem, 5vw, 5rem); color: #08103f; background: url("~@/assets/images/lab-login.png") 74% center / cover no-repeat; }
  .visual-logo { position: relative; z-index: 1; width: 190px; height: auto; }
  .visual-copy { position: relative; z-index: 1; max-width: 600px; margin-top: clamp(1.5rem, 3.5vh, 3rem); }
  .eyebrow { margin: 0 0 1.15rem; color: #64729c; font-size: 11px; font-weight: 700; letter-spacing: 0.32em; }
  .visual-copy h1 { margin: 0; font-size: clamp(2.3rem, 3.8vw, 4rem); line-height: 1.08; letter-spacing: -0.035em; }
  .visual-copy h1 span { display: block; white-space: nowrap; }
  .visual-copy h1 span:last-child { color: #15936d; }
  .visual-description { max-width: 430px; margin: 1.5rem 0 1.4rem; color: #5f6d9a; font-size: 16px; line-height: 1.45; }
  .feature-list { display: grid; gap: 0.8rem; }
  .feature-item { display: flex; align-items: center; gap: 1rem; color: #10174e; }
  .feature-item strong, .feature-item small { display: block; }
  .feature-item strong { font-size: 14px; }
  .feature-item small { margin-top: 0.12rem; color: #7a87ae; font-size: 12px; }
  .feature-icon { display: grid; width: 46px; height: 46px; place-items: center; border-radius: 12px; color: white; font-size: 25px; font-weight: 700; }
  .feature-icon-orders { background: #b6f4dd; color: #009e70; }
  .feature-icon-tests { background: #ae8aeb; }
  .feature-icon-results { background: #5c9ff1; }
  .feature-icon-team { background: #ff9955; }
  .visual-footer { position: absolute; bottom: 2.8rem; left: clamp(2rem, 6vw, 6.5rem); display: flex; align-items: center; gap: 1rem; color: #7280a7; font-size: 10px; font-weight: 700; letter-spacing: 0.22em; }
  .visual-footer i { width: 1px; height: 13px; background: #a9b4d0; }
  .visual-footer strong { color: #17205c; }
  .signin-panel-wrap { position: relative; display: grid; flex: 0 0 38%; min-width: 440px; height: 100%; min-height: 0; overflow: hidden; place-items: center; padding: clamp(1.5rem, 3.5vw, 4rem); background: linear-gradient(135deg, #effefa, #fff 58%, #e4faf1); }
  .signin-panel-wrap::before, .signin-panel-wrap::after { position: absolute; right: -15%; bottom: -20%; left: -15%; height: 42%; content: ""; border: 2px solid rgba(102, 217, 184, 0.18); border-radius: 50% 50% 0 0 / 32% 32% 0 0; transform: rotate(-8deg); }
  .signin-panel-wrap::after { bottom: -27%; border-color: rgba(91, 167, 232, 0.15); transform: rotate(7deg); }
  .signin-panel { position: relative; z-index: 1; width: min(100%, 430px); padding: 3.2rem 2.4rem 3.2rem; text-align: center; background: rgba(255, 255, 255, 0.92); border-radius: 20px; box-shadow: 0 22px 55px rgba(36, 106, 103, 0.12); }
  .panel-logo { width: 190px; height: auto; }
  .signin-panel h2 { margin: 3.5rem 0 0.45rem; color: #090f3e; font-size: 30px; letter-spacing: -0.035em; }
  .panel-subtitle { margin: 0 0 2.7rem; color: #7b86af; font-size: 16px; }
  .legacy-credentials { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
  .microsoft-login-btn { position: relative; display: flex; align-items: center; justify-content: center; gap: 10px; width: 100%; height: 54px; min-height: 54px; margin: 0; padding: 0 3.25rem 0 1.25rem; color: white; font-size: 15px; font-weight: 700; white-space: nowrap; background: #08785f; border: 0; border-radius: 12px; box-shadow: 0 9px 18px rgba(8, 120, 95, 0.2); }
  .microsoft-login-btn:focus { outline: none; }
  .microsoft-login-btn span { display:flex;align-items: center;gap:10px; }
  .microsoft-label { display: inline-block; flex: 0 0 auto; line-height: 1; white-space: nowrap; }
  .microsoft-logo { flex: 0 0 21px; width: 21px; height: 21px; }
  .microsoft-login-btn:hover { color: white; background: #05684f; }
  .button-arrow { position: absolute; right: 1.25rem; font-size: 24px; font-weight: 400; }
  .secure-signin { display: flex; justify-content: center; align-items: center; gap: 0.75rem; margin-top: 3.6rem; padding-top: 1.5rem; border-top: 1px solid #dce8e5; color: #7784ad; font-size: 12px; }
  .secure-icon { width: 54px; height: 54px; }
  .secure-signin strong, .secure-signin small { display: block; }
  .secure-signin small { margin-top: 0.2rem; }
  @media (max-width: 900px) { .signin-visual { flex-basis: 55%; padding: 2.5rem; background-position: 74% center; } .signin-panel-wrap { flex-basis: 45%; min-width: 380px; padding: 2rem; } .visual-copy h1 { font-size: 2.6rem; } }
  @media (max-width: 700px) { #login-page { height: auto; min-height: 100dvh; overflow: visible; } .signin-layout { display: block; height: auto; } .signin-visual { height: 410px; min-height: 410px; padding: 2rem; background-position: 74% center; } .visual-copy { margin-top: 2.6rem; } .visual-copy h1 { font-size: 2.35rem; } .visual-copy h1 span { white-space: normal; } .visual-description, .feature-list, .visual-footer { display: none; } .signin-panel-wrap { height: auto; min-width: 0; padding: 1.5rem; } .signin-panel { padding: 2.5rem 1.5rem; } .signin-panel h2 { margin-top: 2.5rem; } }
}
</style>
<style lang="scss">
#login-bg {
  .login-page {
    .login-left {
      padding: 4rem 0;
      max-width: 280px;
      .company-logo {
        padding-bottom: 1.5rem;
      }
      .inner-wrap {
        p {
          padding-bottom: 0.7rem;
          color: #233a73;
          font-weight: 400;
          font-size: 14px;
        }
        .el-input {
          .el-input__inner {
            height: 50px;

            border-radius: 10px 10px 0 0;
            .el-input__icon {
              width: 470px;
            }
          }
        }
        .forgot {
          display: flex;
          justify-content: space-between;
          align-items: center;
          p {
            margin-left: -4px;
            padding: 0;
            color: #2062ad;
            font-size: 11px;
          }
        }
        .login-btn {
          width: 100%;
          background: linear-gradient(91.35deg, #5277f7 1.15%, #ed4b57 100%);
          color: white;
          font-size: 16px;
          border-radius: 12px;
          padding: 0;
          max-width: 340px;
        }
      }
    }
  }
}
.inner-wrap {
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px white inset !important;
  }
  .show-icon {
    position: relative;
    .el-button {
      position: absolute;
      right: 10px;
      padding: 0px 11px;
      min-height: 17px;
      border: none;
      background: none;
      top: 35%;
      transition: all 5s ease;

      &:hover {
        background: none;
        border: none;
      }
      &:focus {
        background: none;
        border: none;
      }
    }
    .show-eye-icon {
      img {
        width: 135%;
        height: 135%;
      }
    }
    .hide-eye-icon {
      img {
        width: 135%;
        height: 135%;
      }
    }
  }
}
</style>
